import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { formatMoney } from 'app/lib/formatters';

import { PickPresetAmount, pickPresetAmount } from '../../../../actions/stakeTypes';
import { SubmissionState } from '../../../../constants';
import {
  selectAnyHasLockedState,
  selectBets,
  selectSomeHasError,
  selectTotalAmountActive,
} from '../../../../selectors/entries';
import { selectSubmission } from '../../../../selectors/submission';

import styles from '../../FooterBar.module.scss';

// ---

type StoreProps = {
  disabled: boolean;
  price?: number | null;
  totalAmountActive: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<PickPresetAmount>;
};

type BetPresetProps = {
  betId?: number;
  type: string;
  value: number;
};

const BetPreset: FC<BetPresetProps & DispatchProp & StoreProps> = ({
  betId,
  value,
  disabled,
  dispatch,
  price,
  totalAmountActive,
}) => {
  const { lang } = useTranslation();
  const label = formatMoney(value, lang);

  const pickPresentAmountHandler = useCallback(() => {
    dispatch(pickPresetAmount(value, betId));
  }, [value]);

  const dataGaAttributes = {
    ['data-ga-event']: totalAmountActive ? 'cart-bidUnitedAmountSelect' : 'cart-bidAmountSelect',
    ['data-ga-event-content']: betId,
    ['data-ga-event-value']: price,
  };

  return (
    <button type="button"
            data-test="betPresets-button"
            {...dataGaAttributes}
            disabled={disabled}
            className={classNames(styles.barButton, styles.preset)}
            onClick={pickPresentAmountHandler}>
      {label}
    </button>
  );
};

const mapStateToProps = (state: ReduxState, { betId }: BetPresetProps): StoreProps => {
  const hasEntriesErrors = selectSomeHasError(state) || selectAnyHasLockedState(state);
  const price = betId && selectBets(state).get(betId)?.price;

  return {
    price,
    totalAmountActive: selectTotalAmountActive(state),
    disabled: selectSubmission(state) !== SubmissionState.AWAITING || hasEntriesErrors,
  };
};

export default connect(mapStateToProps)(BetPreset);
